import { Controller } from '@hotwired/stimulus';
import { clearBusy, markBusy } from '../js/general/FormBusyState';

const STORAGE_KEY = 'locale';

/**
 * Switches the language without adding a history entry, and returns to the page as it is now, with
 * the search, filters and #anchor its frames or links changed after it loaded. A page the browser
 * brings back from its history in the old language reloads in the new one.
 */
export default class extends Controller {
    static targets = ['targetPath'];

    connect() {
        this.reloadWhenRestored = (event) => {
            if (event.persisted) {
                this.reloadIfOutdated();
            }
        };
        window.addEventListener('pageshow', this.reloadWhenRestored);

        if ('back_forward' === performance.getEntriesByType('navigation')[0]?.type) {
            this.reloadIfOutdated();
        } else {
            writeStoredLocale(document.documentElement.lang);
        }
    }

    disconnect() {
        window.removeEventListener('pageshow', this.reloadWhenRestored);
    }

    async switch(event) {
        if (this.isSendingNatively) {
            return;
        }
        event.preventDefault();
        const button = event.submitter;
        if (!button || this.element.hasAttribute('data-submitting')) {
            return;
        }

        this.targetPathTarget.value = location.pathname + location.search + location.hash;
        markBusy(this.element, button);

        try {
            const response = await fetch(button.formAction, {
                method: 'POST',
                body: new FormData(this.element, button),
                redirect: 'manual',
            });
            if ('opaqueredirect' === response.type) {
                location.replace(this.targetPathTarget.value);

                return;
            }
        } catch {
            // Sent the ordinary way below
        }

        // Let the browser send it, so whatever the server answers is shown
        clearBusy(this.element);
        this.isSendingNatively = true;
        this.element.requestSubmit(button);
    }

    // The language of a page from the history cache differs from the last one shown: the user switched since
    reloadIfOutdated() {
        const storedLocale = readStoredLocale();
        if (storedLocale && storedLocale !== document.documentElement.lang) {
            location.reload();
        }
    }
}

function readStoredLocale() {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function writeStoredLocale(locale) {
    try {
        localStorage.setItem(STORAGE_KEY, locale);
    } catch {
        // Without storage a page from the history cache keeps its language
    }
}
